import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';
import { getErrorWithResponse } from '@confluence/graphql/entry-points/getErrorWithResponse.ts';
import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation.ts';
import { BadStatusError } from '@confluence/network';

import type {
	RevertContentMutation,
	RevertContentMutationVariables,
} from './__types__/RevertContentMutation.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

export const useExperimentalRevertContentMutation = ({
	onCompleted,
}: {
	onCompleted?: (data: RevertContentMutation) => void;
} = {}): [
	(
		options?:
			| {
					variables?: RevertContentMutationVariables | undefined;
					update?:
						| ((
								cache: null,
								result: {
									data?: RevertContentMutation | null | undefined;
								},
						  ) => void)
						| undefined;
					optimisticResponse?:
						| RevertContentMutation
						| ((vars: RevertContentMutationVariables) => RevertContentMutation)
						| undefined;
			  }
			| undefined,
	) => Promise<{
		data: RevertContentMutation | undefined;
	}>,
	{
		data: RevertContentMutation | undefined;
		loading: boolean;
		error: ApolloErrorInterop | undefined;
		called: boolean;
	},
] => {
	return useExperimentalMutation<
		RevertContentMutation,
		RevertContentMutationVariables,
		RevertContentMutationResponse
	>({
		url: (variables) => {
			if (!variables.contentId) {
				throw new Error('contentId is missing');
			}
			return `/wiki/rest/collab/1.0/content/${variables?.contentId}/changes/unpublished`;
		},
		method: 'DELETE',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		parseResult: () => ({ experimentalRevertContent: true }),
		onCompleted,
		transformError: (error) => {
			if (error instanceof BadStatusError) {
				return getErrorWithResponse(
					new Error(`Failed to revert or delete draft: ${error}`),
					error?.response,
				);
			}
			return error;
		},
	});
};

type RevertContentMutationResponse = {};
